import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUseCaseConfig } from "./useUseCaseConfig";
import FieldBuilder from "./FieldBuilder";
import ChecklistBuilder from "./ChecklistBuilder";
import FormPreview from "./FormPreview";
import {
  UseCaseConfig,
  ReviewSampling,
  FormConfigField,
  ChecklistItem,
  USE_CASE_CATEGORY,
  UseCaseCategoryValue,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_LABELS,
  DataSourceTypeValue,
  COMPARISON_MODE,
  ComparisonModeValue,
  INGESTION_SOURCE_TYPE,
  INGESTION_SOURCE_LABELS,
  IngestionSourceTypeValue,
  CLAIM_EXPIRY_OPTIONS,
  OPTION_FIELD_TYPES,
  FIELD_TYPE_LABELS,
  makeUseCaseConfig,
  PermissionsMember,
  ROLE,
  UseCaseAllocation,
} from "../shared/types";
import * as S from "../shared/styles";
import { useAuditAccess } from "../shared/useAuditAccess";
import auditModuleService, {
  SamplingRunConfig,
  SamplingRunResult,
} from "src/services/auditModuleService";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";

// ----------------------------  Tabs  ----------------------------

const BASE_TABS = [
  { key: "overview", label: "Overview" },
  { key: "fields", label: "Fields" },
  { key: "checklist", label: "Checklist" },
  { key: "ingestion", label: "Ingestion Sampling" },
  { key: "sampling", label: "Review Sampling" },
] as const;

const EDIT_ONLY_TABS = [
  { key: "auditors", label: "Auditors" },
  { key: "versions", label: "Versions" },
] as const;

type TabKey = (typeof BASE_TABS)[number]["key"] | (typeof EDIT_ONLY_TABS)[number]["key"];

// ----------------------------  Validation  ----------------------------

interface ConfigErrors {
  fieldErrors: string[];
  checklistErrors: string[];
}

function validateConfig(fields: FormConfigField[], checklist: ChecklistItem[]): ConfigErrors {
  const fieldErrors: string[] = [];
  const checklistErrors: string[] = [];
  if (fields.length === 0) fieldErrors.push("Add at least one field.");
  fields.forEach((f, i) => {
    const tag = "Field " + (i + 1);
    if (!f.label?.trim()) fieldErrors.push(tag + ": label is required.");
    if (OPTION_FIELD_TYPES.indexOf(f.type) !== -1 && f.options.filter(Boolean).length === 0)
      fieldErrors.push(tag + " (" + FIELD_TYPE_LABELS[f.type] + "): needs at least one option.");
    if (f.min != null && f.max != null && f.min > f.max)
      fieldErrors.push(tag + ": min must be \u2264 max.");
    if (f.step != null && f.step <= 0) fieldErrors.push(tag + ": step must be greater than 0.");
    if (f.char_limit != null && f.char_limit <= 0)
      fieldErrors.push(tag + ": character limit must be greater than 0.");
  });
  checklist.forEach((c, i) => {
    if (!c.label?.trim())
      checklistErrors.push("Checklist item " + (i + 1) + ": label is required.");
  });
  return { fieldErrors, checklistErrors };
}

// ----------------------------  Main Component  ----------------------------

const UseCaseEditor: React.FC = () => {
  const { useCaseId } = useParams<{ useCaseId?: string }>();
  const navigate = useNavigate();
  const {
    configs,
    isLoading,
    isError,
    fetchError,
    authError,
    configById,
    saveUseCase,
    updateUseCase,
    isSaving,
    saveSuccess,
    saveError,
    resetSave,
    retryFetch,
  } = useUseCaseConfig();

  const editing = Boolean(useCaseId);
  const { canManageUseCase, isAdmin } = useAuditAccess();

  // Initialise state once configs are loaded (for edit mode).
  const [initialised, setInitialised] = useState(!editing);
  const [tab, setTab] = useState<TabKey>("overview");
  const [uc, setUc] = useState<UseCaseConfig>(() => makeUseCaseConfig());
  const [showSaveError, setShowSaveError] = useState(false);

  // When editing, seed state from the store once loading finishes.
  useEffect(() => {
    if (!editing || initialised || isLoading) return;
    const existing = configById(useCaseId!);
    if (existing) {
      setUc({
        ...existing,
        fields: existing.fields.map((f) => ({ ...f, options: [...f.options] })),
        checklist_items: existing.checklist_items.map((c) => ({ ...c })),
      });
    }
    // Mark initialised even when not found so the not-found branch can render.
    setInitialised(true);
  }, [editing, initialised, isLoading, useCaseId, configById]);

  const setU = (patch: Partial<UseCaseConfig>) => {
    setUc((prev) => ({ ...prev, ...patch }));
    // Dismiss save error when the user modifies the form.
    if (showSaveError) setShowSaveError(false);
  };

  // Clear any stale save state left over from a prior session.
  useEffect(() => {
    resetSave();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect on successful save.
  useEffect(() => {
    if (saveSuccess) {
      resetSave();
      navigate("/vlmAudit/config");
    }
  }, [saveSuccess, navigate, resetSave]);

  // Show error banner on save failure; reset slice so user can retry.
  useEffect(() => {
    if (saveError) {
      setShowSaveError(true);
      resetSave();
    }
  }, [saveError, resetSave]);

  // ----------------------------  Validation  ----------------------------

  const nameError = useMemo(() => {
    const name = uc.name?.trim();
    if (!name) return "Name is required.";
    if (name.length > 100) return "Name must be 100 characters or fewer.";
    const clash = configs.some(
      (u) => u.id !== uc.id && u.name.trim().toLowerCase() === name.toLowerCase(),
    );
    if (clash) return "A use case with this name already exists.";
    return null;
  }, [uc.name, uc.id, configs]);

  const { fieldErrors, checklistErrors } = useMemo(
    () => validateConfig(uc.fields, uc.checklist_items),
    [uc.fields, uc.checklist_items],
  );

  // ----------------------------  Sampling helpers  ----------------------------

  const sampling = uc.review_sampling;
  const clampPct = (v: string | number) => Math.max(0, Math.min(100, Number(v) || 0));

  const setSampling = (patch: Partial<ReviewSampling>) => {
    const next: ReviewSampling = { ...sampling, ...patch };
    // Disabling 2nd also disables 3rd.
    if (patch.second_enabled === false) {
      next.third_enabled = false;
    }
    const effSecond = next.second_enabled ? next.second_pct : 0;
    const effThird = next.second_enabled && next.third_enabled ? next.third_pct : 0;
    const maxR = effThird > 0 ? 3 : effSecond > 0 ? 2 : 1;
    setUc((prev) => ({ ...prev, review_sampling: next, inter_rater_count: maxR }));
  };

  // Auditors tab: stored shares (only > 0%) must total 100 when anyone is assigned.
  const allocationTotal = (uc.allocations || []).reduce((n, a) => n + a.percent, 0);
  const allocationError =
    (uc.allocations || []).length > 0 && allocationTotal !== 100
      ? "Auditor allocations must total 100% (currently " + allocationTotal + "%)."
      : null;

  const samplingError =
    sampling.second_enabled && sampling.second_pct <= 0
      ? "Enter a 2nd-review % greater than 0, or disable the 2nd review."
      : sampling.second_enabled && sampling.third_enabled && sampling.third_pct <= 0
        ? "Enter a 3rd-review % greater than 0, or disable the 3rd review."
        : sampling.second_enabled &&
            sampling.third_enabled &&
            sampling.third_pct > sampling.second_pct
          ? "The 3rd-review % cannot exceed the 2nd-review %."
          : null;

  const allErrors = [
    ...(nameError ? [nameError] : []),
    ...fieldErrors,
    ...checklistErrors,
    ...(samplingError ? [samplingError] : []),
    ...(allocationError ? [allocationError] : []),
  ];
  const canSave = allErrors.length === 0;

  const persist = (forceActivate?: boolean) => {
    const ts = new Date().toISOString();
    const toSave: UseCaseConfig = {
      ...uc,
      status: forceActivate ? "active" : uc.status,
      updated_at: ts,
    };
    if (editing) {
      updateUseCase(toSave);
    } else {
      saveUseCase(toSave);
    }
  };

  // ----------------------------  Tab labels with counts  ----------------------------

  const tabs = editing ? [...BASE_TABS, ...EDIT_ONLY_TABS] : [...BASE_TABS];

  const tabLabels: Record<TabKey, string> = {
    overview: "Overview",
    fields: "Fields (" + uc.fields.length + ")",
    checklist: "Checklist (" + uc.checklist_items.length + ")",
    ingestion: "Ingestion Sampling",
    sampling: "Review Sampling",
    auditors: "Auditors",
    versions: "Versions",
  };

  // ----------------------------  Loading / Not Found  ----------------------------

  if (editing && (isLoading || !initialised)) {
    return (
      <Row width="fill" alignmentHorizontal="center">
        <Column height="60vh" alignmentVertical="center">
          <Loader />
        </Column>
      </Row>
    );
  }

  if (editing && initialised && isError) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          {authError
            ? "Unable to resolve your identity. Please refresh the page or try again later."
            : "Failed to load use case configurations."}
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          {fetchError && (
            <button style={S.btnPrimary} onClick={retryFetch}>
              Retry
            </button>
          )}
          <button style={S.btnSecondary} onClick={() => navigate("/vlmAudit/config")}>
            Back to list
          </button>
        </div>
      </div>
    );
  }

  if (editing && initialised && !configById(useCaseId!)) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          Use case not found. It may have been deleted.
        </div>
        <button style={S.btnSecondary} onClick={() => navigate("/vlmAudit/config")}>
          Back to list
        </button>
      </div>
    );
  }

  // A Program Owner may configure only the use cases an Admin granted them.
  if (editing && !canManageUseCase(useCaseId!)) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          You do not have access to configure this use case. Ask an Admin to grant it to you.
        </div>
        <button style={S.btnSecondary} onClick={() => navigate("/vlmAudit/config")}>
          Back to list
        </button>
      </div>
    );
  }

  // ----------------------------  Render  ----------------------------

  return (
    <div style={S.pageWrapper}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div>
          <h1 style={S.pageTitle}>{editing ? uc.name || "Edit Use Case" : "New Use Case"}</h1>
          <p style={S.pageSubtitle}>
            Configure settings, audit fields, and checklist — all in one place.
          </p>
        </div>
        <button
          style={{ ...S.btnSecondary, fontSize: "0.875rem", padding: "8px 16px" }}
          onClick={() => navigate("/vlmAudit/config")}
        >
          ← All use cases
        </button>
      </div>

      {/* Tabs */}
      <div style={S.tabRow}>
        <div style={S.tabGroup}>
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={tab === t.key ? S.tabActive : S.tabInactive}
            >
              {tabLabels[t.key]}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {tab === "overview" && (
        <div style={{ ...S.configCard, maxWidth: "52rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
            {/* Name */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Use Case Name<span style={S.formLabelRequired}>*</span>
              </label>
              <input
                style={S.formInput}
                value={uc.name}
                onChange={(e) => setU({ name: e.target.value })}
                placeholder="e.g. Safety Genie: Cage Inspection"
              />
              <p style={S.formHelp}>Unique, max 100 characters.</p>
            </div>
            {/* Category */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Category<span style={S.formLabelRequired}>*</span>
              </label>
              <select
                style={S.formSelect}
                value={uc.category}
                onChange={(e) => setU({ category: e.target.value as UseCaseCategoryValue })}
              >
                {Object.values(USE_CASE_CATEGORY).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            {/* Description — full width */}
            <div style={{ ...S.formRow, gridColumn: "1 / -1" }}>
              <label style={S.formLabel}>Description</label>
              <textarea
                style={S.formTextarea}
                value={uc.description}
                onChange={(e) => setU({ description: e.target.value })}
                placeholder="What does this audit evaluate?"
              />
            </div>
            {/* Data Source Type */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Data Source Type<span style={S.formLabelRequired}>*</span>
              </label>
              <select
                style={S.formSelect}
                value={uc.data_source_type}
                onChange={(e) => setU({ data_source_type: e.target.value as DataSourceTypeValue })}
              >
                {Object.values(DATA_SOURCE_TYPE).map((v) => (
                  <option key={v} value={v}>
                    {DATA_SOURCE_LABELS[v as DataSourceTypeValue]}
                  </option>
                ))}
              </select>
              <p style={S.formHelp}>Determines the workspace media viewer.</p>
            </div>
            {/* Comparison Mode */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Comparison Mode<span style={S.formLabelRequired}>*</span>
              </label>
              <select
                style={S.formSelect}
                value={uc.comparison_mode}
                onChange={(e) => setU({ comparison_mode: e.target.value as ComparisonModeValue })}
              >
                <option value={COMPARISON_MODE.SIMPLE}>Simple — correct / incorrect</option>
                <option value={COMPARISON_MODE.OVERRIDE}>Override — AI vs human</option>
              </select>
            </div>
            {/* Claim Expiry */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Claim Expiry<span style={S.formLabelRequired}>*</span>
              </label>
              <select
                style={S.formSelect}
                value={String(uc.claim_expiry_minutes)}
                onChange={(e) => setU({ claim_expiry_minutes: Number(e.target.value) })}
              >
                {CLAIM_EXPIRY_OPTIONS.map((o) => (
                  <option key={o.value} value={String(o.value)}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Active toggle — full width */}
            <div style={{ ...S.formRow, gridColumn: "1 / -1", paddingTop: "4px" }}>
              <label style={{ fontSize: "0.875rem", color: "#374151", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  style={S.formCheckbox}
                  checked={uc.status === "active"}
                  onChange={(e) => setU({ status: e.target.checked ? "active" : "inactive" })}
                />
                Active (available in the audit queue)
              </label>
            </div>
          </div>
          {nameError && <div style={{ ...S.formError, marginTop: "8px" }}>{nameError}</div>}
        </div>
      )}

      {/* Fields Tab — split: builder left, preview right */}
      {tab === "fields" && (
        <div style={splitLayout}>
          <div style={splitLeft}>
            <div style={sectionTitle}>Fields</div>
            <FieldBuilder fields={uc.fields} onChange={(fields) => setU({ fields })} />
            {fieldErrors.length > 0 && (
              <div style={{ ...S.formError, marginTop: "8px" }}>
                {fieldErrors.map((e) => (
                  <div key={e}>• {e}</div>
                ))}
              </div>
            )}
          </div>
          <div style={splitRight}>
            <div style={sectionTitle}>Live Preview</div>
            <div style={previewBox}>
              <FormPreview config={uc} showChecklist={false} />
            </div>
          </div>
        </div>
      )}

      {/* Checklist Tab — split: builder left, preview right */}
      {tab === "checklist" && (
        <div style={splitLayout}>
          <div style={splitLeft}>
            <div style={sectionTitle}>Checklist (optional)</div>
            <ChecklistBuilder
              items={uc.checklist_items}
              onChange={(checklist_items) => setU({ checklist_items })}
            />
            {checklistErrors.length > 0 && (
              <div style={{ ...S.formError, marginTop: "8px" }}>
                {checklistErrors.map((e) => (
                  <div key={e}>• {e}</div>
                ))}
              </div>
            )}
          </div>
          <div style={splitRight}>
            <div style={sectionTitle}>Preview (Pass / Fail / N/A)</div>
            <div style={previewBox}>
              {uc.checklist_items.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {uc.checklist_items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: "0.875rem",
                      }}
                    >
                      <span style={{ color: "#374151" }}>{item.label || "(untitled item)"}</span>
                      <span style={{ display: "flex", gap: "4px" }}>
                        {["Pass", "Fail", "N/A"].map((r) => (
                          <span
                            key={r}
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              border: "1px solid #e5e7eb",
                              borderRadius: "4px",
                              color: "#9ca3af",
                            }}
                          >
                            {r}
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Add items to preview.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ingestion Tab */}
      {tab === "ingestion" && (
        <div style={{ ...S.configCard, maxWidth: "42rem" }}>
          <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "16px" }}>
            Configure how audit samples for this use case are ingested into the review queue.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
            {/* Ingestion Source Type */}
            <div style={S.formRow}>
              <label style={S.formLabel}>Ingestion Source Type</label>
              <select
                style={S.formSelect}
                value={uc.ingestion_source_type}
                onChange={(e) =>
                  setU({ ingestion_source_type: e.target.value as IngestionSourceTypeValue })
                }
              >
                {Object.values(INGESTION_SOURCE_TYPE).map((v) => (
                  <option key={v} value={v}>
                    {INGESTION_SOURCE_LABELS[v as IngestionSourceTypeValue]}
                  </option>
                ))}
              </select>
              <p style={S.formHelp}>
                Where a sampling run draws this use case&apos;s records from.
              </p>
            </div>
            {/* Ingestion Source */}
            <div style={S.formRow}>
              <label style={S.formLabel}>Ingestion Source</label>
              <input
                style={S.formInput}
                value={uc.ingestion_source}
                onChange={(e) => setU({ ingestion_source: e.target.value })}
                placeholder="e.g. SGInspectionSync-beta-na"
              />
              <p style={S.formHelp}>
                Concrete pointer to that source (table name, S3 URI, or batch id).
              </p>
            </div>
          </div>
          {/* Sampling endpoints require the audit-admin group; a Program Owner would get a 403. */}
          {editing && isAdmin && <SamplingRunPanel useCaseId={uc.id} />}
        </div>
      )}

      {/* Review Sampling Tab */}
      {tab === "sampling" && (
        <div style={{ ...S.configCard, maxWidth: "42rem" }}>
          <div style={sectionTitle}>Review Sampling</div>
          <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "16px" }}>
            Most cases get a single review. Enable a tier to sample a percentage of cases for an
            independent 2nd (and 3rd) review to measure inter-rater reliability.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
            {/* 2nd review */}
            <div>
              <label style={samplingCheckLabel}>
                <input
                  type="checkbox"
                  style={S.formCheckbox}
                  checked={!!sampling.second_enabled}
                  onChange={(e) => setSampling({ second_enabled: e.target.checked })}
                />
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
                  Enable 2nd review
                </span>
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "8px" }}>
                <input
                  type="number"
                  style={{ ...S.formInput, width: "80px" }}
                  value={sampling.second_enabled ? sampling.second_pct : ""}
                  onChange={(e) => setSampling({ second_pct: clampPct(e.target.value) })}
                  placeholder="0"
                  disabled={!sampling.second_enabled}
                  min={0}
                  max={100}
                />
                <span style={{ color: "#6b7280" }}>%</span>
              </div>
              <p style={{ ...S.formHelp, marginTop: "4px" }}>% of cases needing a 2nd review.</p>
              <div style={{ marginTop: "12px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#4b5563",
                    marginBottom: "4px",
                  }}
                >
                  2nd review reviewer
                </label>
                <select
                  style={S.formSelect}
                  value={sampling.second_reviewer}
                  onChange={(e) => setSampling({ second_reviewer: e.target.value })}
                  disabled={!sampling.second_enabled}
                >
                  <option value="">Any authorized auditor</option>
                </select>
              </div>
            </div>

            {/* 3rd review */}
            <div style={{ opacity: sampling.second_enabled ? 1 : 0.5 }}>
              <label
                style={{
                  ...samplingCheckLabel,
                  cursor: sampling.second_enabled ? "pointer" : "not-allowed",
                }}
              >
                <input
                  type="checkbox"
                  style={S.formCheckbox}
                  checked={!!sampling.third_enabled}
                  disabled={!sampling.second_enabled}
                  onChange={(e) => setSampling({ third_enabled: e.target.checked })}
                />
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
                  Enable 3rd review
                </span>
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "8px" }}>
                <input
                  type="number"
                  style={{ ...S.formInput, width: "80px" }}
                  value={sampling.third_enabled ? sampling.third_pct : ""}
                  onChange={(e) => setSampling({ third_pct: clampPct(e.target.value) })}
                  placeholder="0"
                  disabled={!sampling.second_enabled || !sampling.third_enabled}
                  min={0}
                  max={100}
                />
                <span style={{ color: "#6b7280" }}>%</span>
              </div>
              <p style={{ ...S.formHelp, marginTop: "4px" }}>Must be &le; the 2nd-review %.</p>
              <div style={{ marginTop: "12px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#4b5563",
                    marginBottom: "4px",
                  }}
                >
                  3rd review reviewer
                </label>
                <select
                  style={S.formSelect}
                  value={sampling.third_reviewer}
                  onChange={(e) => setSampling({ third_reviewer: e.target.value })}
                  disabled={!sampling.second_enabled || !sampling.third_enabled}
                >
                  <option value="">Any authorized auditor</option>
                </select>
              </div>
            </div>
          </div>

          {samplingError && <div style={{ ...S.formError, marginTop: "8px" }}>{samplingError}</div>}

          {sampling.second_enabled ? (
            <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "12px" }}>
              Result: ~{100 - (sampling.second_pct || 0)}% single-review, ~
              {(sampling.second_pct || 0) - (sampling.third_enabled ? sampling.third_pct || 0 : 0)}%
              double-review, ~{sampling.third_enabled ? sampling.third_pct || 0 : 0}% triple-review.
            </div>
          ) : (
            <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "12px" }}>
              All cases get a single review (sampling disabled).
            </div>
          )}
        </div>
      )}

      {/* Auditors Tab */}
      {tab === "auditors" && editing && (
        <AuditorsTab
          useCaseId={uc.id}
          useCaseName={uc.name}
          allocations={uc.allocations || []}
          onChange={(allocations) => setU({ allocations })}
        />
      )}

      {/* Versions Tab */}
      {tab === "versions" && editing && <VersionsTab useCaseId={uc.id} />}

      {/* Save error banner */}
      {showSaveError && (
        <div style={saveErrorBanner}>
          <span>Failed to save. Please check your connection and try again.</span>
          <button
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#991b1b",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
            onClick={() => setShowSaveError(false)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Footer — Save actions (hidden on self-managed tabs) */}
      {tab !== "versions" && (
        <div style={S.wizardNav}>
          <button style={S.btnSecondary} onClick={() => navigate("/vlmAudit/config")}>
            Cancel
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {allErrors.length > 0 && (
              <span style={{ fontSize: "0.75rem", color: "#ef4444" }}>
                {allErrors.length} issue(s) to resolve
              </span>
            )}
            <button
              style={{ ...S.btnOutline, opacity: canSave ? 1 : 0.5 }}
              disabled={!canSave || isSaving}
              onClick={() => persist()}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              style={{ ...S.btnPrimary, opacity: canSave ? 1 : 0.5 }}
              disabled={!canSave || isSaving}
              onClick={() => persist(true)}
            >
              {isSaving ? "Saving..." : "Save & Activate"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ----------------------------  AuditorsTab  ----------------------------

// ISO-8601 week label (e.g. "2026-W39") — default period start for a sampling run.
const isoWeek = (d: Date): string => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const week =
    1 +
    Math.round(
      ((date.getTime() - firstThursday.getTime()) / 86400000 -
        3 +
        ((firstThursday.getUTCDay() + 6) % 7)) /
        7,
    );
  return date.getUTCFullYear() + "-W" + String(week).padStart(2, "0");
};

interface AuditorsTabProps {
  useCaseId: string;
  useCaseName: string;
  allocations: UseCaseAllocation[];
  onChange: (allocations: UseCaseAllocation[]) => void;
}

const isAdminMember = (m: PermissionsMember): boolean => !!m.elevated || m.role === ROLE.ADMIN;

const AuditorsTab: React.FC<AuditorsTabProps> = ({
  useCaseId,
  useCaseName,
  allocations,
  onChange,
}) => {
  const [search, setSearch] = React.useState("");
  const [group, setGroup] = React.useState<"auditors" | "admin">("auditors");
  const [page, setPage] = React.useState(0);
  // Aliases assigned when the tab opened; listed first (stable while editing).
  const [assigned] = React.useState<Set<string>>(() => new Set(allocations.map((a) => a.login)));
  // Checked rows (a checked row at 0% is not stored).
  const [selected, setSelected] = React.useState<Set<string>>(
    () => new Set(allocations.map((a) => a.login)),
  );
  const [pct, setPct] = React.useState<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    allocations.forEach((a) => {
      out[a.login] = a.percent;
    });
    return out;
  });
  const [auditors, setAuditors] = React.useState<PermissionsMember[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);

  // Push the stored shape up: checked rows with a share > 0 only.
  const emit = (nextSelected: Set<string>, nextPct: Record<string, number>) => {
    setSelected(nextSelected);
    setPct(nextPct);
    onChange(
      Array.from(nextSelected)
        .filter((l) => (nextPct[l] || 0) > 0)
        .map((l) => ({ login: l, percent: nextPct[l] })),
    );
  };

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    auditModuleService
      .getPermissionMembers()
      .then((members) => {
        if (!active) return;
        const pool = members.filter(
          (m) => isAdminMember(m) || m.assigned_use_case_ids.includes(useCaseId),
        );
        setAuditors(pool);
        // Drop stored shares for aliases no longer authorized on this use case.
        const inPool = new Set(pool.map((m) => m.login));
        if (allocations.some((a) => !inPool.has(a.login))) {
          emit(new Set(Array.from(selected).filter((l) => inPool.has(l))), pct);
        }
      })
      .catch(() => active && setLoadError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [useCaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (login: string) => {
    const next = new Set(selected);
    const nextPct = { ...pct };
    if (next.has(login)) {
      next.delete(login);
      delete nextPct[login];
    } else {
      next.add(login);
    }
    emit(next, nextPct);
  };

  const evenSplit = () => {
    const sel = Array.from(selected);
    if (!sel.length) return;
    const base = Math.floor(100 / sel.length);
    const rem = 100 - base * sel.length;
    const next: Record<string, number> = {};
    sel.forEach((l, i) => {
      next[l] = base + (i < rem ? 1 : 0);
    });
    emit(selected, next);
  };

  const total = Array.from(selected).reduce((n, l) => n + (pct[l] || 0), 0);
  // Search by alias or name; admins are listed in their own section.
  const query = search.trim().toLowerCase();
  const visible = auditors
    .filter(
      (a) =>
        !query || a.login.toLowerCase().includes(query) || a.name.toLowerCase().includes(query),
    )
    .sort((x, y) => Number(assigned.has(y.login)) - Number(assigned.has(x.login)));
  const adminRows = visible.filter(isAdminMember);
  const auditorRows = visible.filter((a) => !isAdminMember(a));

  const renderRow = (a: PermissionsMember) => {
    const checked = selected.has(a.login);
    return (
      <div key={a.login} style={auditorRow}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
            userSelect: "none" as const,
          }}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={() => toggle(a.login)}
            style={{ width: "18px", height: "18px", accentColor: "#0f7b8a" }}
          />
          <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#1f2937" }}>{a.name}</span>
          <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>{a.login}</span>
        </label>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input
            type="number"
            min={0}
            max={100}
            value={checked ? pct[a.login] ?? 0 : 0}
            onChange={(e) => emit(selected, { ...pct, [a.login]: Number(e.target.value) || 0 })}
            disabled={!checked}
            style={{
              ...S.formInput,
              width: "64px",
              textAlign: "right" as const,
              opacity: checked ? 1 : 0.4,
            }}
          />
          <span style={{ fontSize: "0.875rem", color: "#6b7280", marginLeft: "4px" }}>%</span>
        </div>
      </div>
    );
  };

  // Admins and auditors are on separate sub-tabs, paged AUDITORS_PAGE_SIZE at a time.
  const groupRows = group === "admin" ? adminRows : auditorRows;
  const pageCount = Math.max(1, Math.ceil(groupRows.length / AUDITORS_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = groupRows.slice(
    currentPage * AUDITORS_PAGE_SIZE,
    (currentPage + 1) * AUDITORS_PAGE_SIZE,
  );

  const switchGroup = (g: "auditors" | "admin") => {
    setGroup(g);
    setPage(0);
  };

  return (
    <div style={{ maxWidth: "42rem" }}>
      {/* Admin / Auditors sub-tabs */}
      <div style={{ ...S.tabGroup, marginBottom: "16px" }}>
        <button
          style={group === "auditors" ? S.tabActive : S.tabInactive}
          onClick={() => switchGroup("auditors")}
        >
          Auditors ({auditorRows.length})
        </button>
        <button
          style={group === "admin" ? S.tabActive : S.tabInactive}
          onClick={() => switchGroup("admin")}
        >
          Admin ({adminRows.length})
        </button>
      </div>

      <input
        type="text"
        placeholder="Search by alias or name"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
        style={{ ...S.formInput, width: "100%", marginBottom: "12px" }}
      />

      {/* Total (kept at the top so it stays visible with long member lists) */}
      <div style={totalBar}>
        <div>
          <div style={{ color: "#4b5563" }}>
            Cases should be distributed 100% among the selected users.
          </div>
          <button
            style={{
              background: "none",
              border: "none",
              cursor: selected.size > 0 ? "pointer" : "default",
              color: selected.size > 0 ? "#0f7b8a" : "#d1d5db",
              fontSize: "0.875rem",
              padding: 0,
              marginTop: "4px",
            }}
            disabled={selected.size === 0}
            onClick={evenSplit}
          >
            Distribute evenly
            {selected.size > 0 ? " (" + Math.floor(100 / selected.size) + "% each)" : ""}
          </button>
        </div>
        <span
          style={{
            fontWeight: 700,
            fontSize: "1.125rem",
            color: total === 100 ? "#111827" : "#ef4444",
          }}
        >
          {total}%
        </span>
      </div>

      {/* Member rows */}
      {loading && (
        <div style={{ fontSize: "0.875rem", color: "#6b7280", padding: "12px 0" }}>
          Loading auditors…
        </div>
      )}
      {loadError && (
        <div style={{ fontSize: "0.875rem", color: "#ef4444", padding: "12px 0" }}>
          Could not load auditors. Please try again.
        </div>
      )}
      {!loading && !loadError && auditors.length === 0 && (
        <div style={{ fontSize: "0.875rem", color: "#6b7280", padding: "12px 0" }}>
          No auditors are authorized on this use case yet. Authorize members on{" "}
          <strong>{useCaseName || "this use case"}</strong> in Permissions &amp; Access first.
        </div>
      )}
      {!loading && !loadError && auditors.length > 0 && groupRows.length === 0 && (
        <div style={{ fontSize: "0.875rem", color: "#6b7280", padding: "12px 0" }}>
          {query ? 'No members match "' + search + '".' : "No members in this group."}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {pageRows.map(renderRow)}
      </div>

      {/* Pager */}
      {pageCount > 1 && (
        <div style={pagerRow}>
          <button
            style={{ ...S.btnSecondary, opacity: currentPage > 0 ? 1 : 0.5 }}
            disabled={currentPage === 0}
            onClick={() => setPage(currentPage - 1)}
          >
            {"\u2039"} Prev
          </button>
          <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
            Page {currentPage + 1} of {pageCount} ({groupRows.length} members)
          </span>
          <button
            style={{ ...S.btnSecondary, opacity: currentPage < pageCount - 1 ? 1 : 0.5 }}
            disabled={currentPage >= pageCount - 1}
            onClick={() => setPage(currentPage + 1)}
          >
            Next {"\u203A"}
          </button>
        </div>
      )}
    </div>
  );
};

// ----------------------------  SamplingRunPanel  ----------------------------

type SamplingConfidence = SamplingRunConfig["confidence"];
type SamplingMargin = SamplingRunConfig["marginOfError"];
type SamplingPeriodValue = SamplingRunConfig["period"];

const SAMPLING_PERIODS: SamplingPeriodValue[] = ["Weekly", "Bi-weekly", "Monthly"];
const SAMPLING_CONFIDENCES: SamplingConfidence[] = ["90", "95", "99"];
const SAMPLING_MARGINS: SamplingMargin[] = ["2", "5"];

const errorDetail = (err: unknown): string => {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = e?.response?.data?.detail;
  return typeof detail === "string" ? detail : e?.message || "Request failed";
};

// Draws the period's population into the review queue. Run is only enabled once a
// preview for the same parameters has shown what will be written.
export const SamplingRunPanel: React.FC<{ useCaseId: string }> = ({ useCaseId }) => {
  const [period, setPeriod] = React.useState<SamplingPeriodValue>("Weekly");
  const [periodStart, setPeriodStart] = React.useState(() => isoWeek(new Date()));
  // 99% / ±2% samples every record of a small population (up to ~65), which is
  // what initial testing wants.
  const [confidence, setConfidence] = React.useState<SamplingConfidence>("99");
  const [margin, setMargin] = React.useState<SamplingMargin>("2");
  const [preview, setPreview] = React.useState<SamplingRunResult | null>(null);
  const [runResult, setRunResult] = React.useState<SamplingRunResult | null>(null);
  const [busy, setBusy] = React.useState<"preview" | "run" | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const config: SamplingRunConfig = {
    useCaseId,
    confidence,
    marginOfError: margin,
    period,
    periodStart: periodStart.trim(),
    // Fixed so a rerun of the same period draws the same records.
    seed: 1,
  };

  const resetResults = () => {
    setPreview(null);
    setRunResult(null);
    setError(null);
  };

  const call = async (kind: "preview" | "run") => {
    setBusy(kind);
    setError(null);
    try {
      if (kind === "preview") {
        setPreview(await auditModuleService.previewSampling(config));
        setRunResult(null);
      } else {
        setRunResult(await auditModuleService.runSampling(config));
      }
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setBusy(null);
    }
  };

  const canRun = !!preview && preview.sampleSize > 0 && !runResult && busy === null;

  return (
    <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "20px", paddingTop: "16px" }}>
      <div style={sectionTitle}>Run Sampling</div>
      <p style={S.formHelp}>
        Draw a sample of this period&apos;s records into the review queue. Preview first; it writes
        nothing.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px 24px",
          marginTop: "12px",
        }}
      >
        <div style={S.formRow}>
          <label style={S.formLabel}>Period</label>
          <select
            style={S.formSelect}
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value as SamplingPeriodValue);
              resetResults();
            }}
          >
            {SAMPLING_PERIODS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div style={S.formRow}>
          <label style={S.formLabel}>Period Start</label>
          <input
            style={S.formInput}
            value={periodStart}
            onChange={(e) => {
              setPeriodStart(e.target.value);
              resetResults();
            }}
            placeholder="e.g. 2026-W40 or 2026-10-01"
          />
          <p style={S.formHelp}>ISO week (2026-W40) or date (2026-10-01).</p>
        </div>
        <div style={S.formRow}>
          <label style={S.formLabel}>Confidence</label>
          <select
            style={S.formSelect}
            value={confidence}
            onChange={(e) => {
              setConfidence(e.target.value as SamplingConfidence);
              resetResults();
            }}
          >
            {SAMPLING_CONFIDENCES.map((c) => (
              <option key={c} value={c}>
                {c}%
              </option>
            ))}
          </select>
        </div>
        <div style={S.formRow}>
          <label style={S.formLabel}>Margin of Error</label>
          <select
            style={S.formSelect}
            value={margin}
            onChange={(e) => {
              setMargin(e.target.value as SamplingMargin);
              resetResults();
            }}
          >
            {SAMPLING_MARGINS.map((m) => (
              <option key={m} value={m}>
                ±{m}%
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
        <button
          style={{ ...S.btnOutline, opacity: busy || !config.periodStart ? 0.5 : 1 }}
          disabled={busy !== null || !config.periodStart}
          onClick={() => call("preview")}
        >
          {busy === "preview" ? "Previewing..." : "Preview"}
        </button>
        <button
          style={{ ...S.btnPrimary, opacity: canRun ? 1 : 0.5 }}
          disabled={!canRun}
          onClick={() => call("run")}
        >
          {busy === "run" ? "Running..." : "Run Sampling"}
        </button>
      </div>

      {error && <div style={{ ...S.formError, marginTop: "8px" }}>{error}</div>}
      {preview && (
        <p style={{ fontSize: "0.875rem", color: "#374151", marginTop: "12px" }}>
          Population <strong>{preview.population}</strong>, sample size{" "}
          <strong>{preview.sampleSize}</strong>
          {preview.sampleSize === 0 && " (nothing to sample for this period)"}.
        </p>
      )}
      {runResult && (
        <p style={{ fontSize: "0.875rem", color: "#374151", marginTop: "4px" }}>
          Ingested <strong>{runResult.ingested}</strong>, skipped (already sampled){" "}
          <strong>{runResult.skipped}</strong>, failed{" "}
          <strong style={{ color: runResult.failed ? "#ef4444" : undefined }}>
            {runResult.failed}
          </strong>
          {runResult.batchId && <> — batch {runResult.batchId}</>}.
        </p>
      )}
    </div>
  );
};

// ----------------------------  VersionsTab  ----------------------------

const PLACEHOLDER_VERSIONS = [
  {
    id: "v3",
    version: 3,
    is_active: true,
    fields_count: 6,
    updated_at: new Date().toISOString(),
  },
  {
    id: "v2",
    version: 2,
    is_active: false,
    fields_count: 5,
    updated_at: new Date(Date.now() - 7 * 86400000).toISOString(),
  },
  {
    id: "v1",
    version: 1,
    is_active: false,
    fields_count: 4,
    updated_at: new Date(Date.now() - 30 * 86400000).toISOString(),
  },
];

const VersionsTab: React.FC<{ useCaseId: string }> = () => {
  return (
    <div style={{ ...S.configCard, maxWidth: "42rem" }}>
      <div style={sectionTitle}>Version History</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {PLACEHOLDER_VERSIONS.map((v) => (
          <div key={v.id} style={versionRow}>
            <span style={{ color: "#374151", fontSize: "0.875rem" }}>
              v{v.version}
              {v.is_active && <span style={activeBadge}>Active</span>}
              <span style={{ fontSize: "0.75rem", color: "#9ca3af", marginLeft: "8px" }}>
                {v.fields_count} fields &middot; {new Date(v.updated_at).toLocaleDateString()}
              </span>
            </span>
            {!v.is_active && <button style={rollbackBtn}>Roll back to this</button>}
          </div>
        ))}
      </div>
      <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "8px" }}>
        Rolling back copies a version into a new active version — history is never overwritten.
      </p>
    </div>
  );
};

// ----------------------------  Layout Styles  ----------------------------

const splitLayout: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "24px",
  alignItems: "start",
};

const splitLeft: React.CSSProperties = {
  minWidth: 0,
};

const splitRight: React.CSSProperties = {
  minWidth: 0,
  position: "sticky",
  top: "80px",
};

const sectionTitle: React.CSSProperties = {
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "8px",
};

const previewBox: React.CSSProperties = {
  background: "#f9fafb",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  padding: "16px",
};

const saveErrorBanner: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#fee2e2",
  border: "1px solid #fca5a5",
  borderRadius: "8px",
  padding: "12px 16px",
  marginTop: "16px",
  maxWidth: "52rem",
  fontSize: "0.875rem",
  color: "#991b1b",
};

const samplingCheckLabel: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  userSelect: "none",
  marginBottom: "4px",
};

const AUDITORS_PAGE_SIZE = 10;

const totalBar: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "0.875rem",
  padding: "12px 16px",
  marginBottom: "16px",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  background: "#f9fafb",
};

const pagerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "16px",
};

const auditorRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  padding: "10px 16px",
  background: "white",
};

const versionRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f9fafb",
  borderRadius: "6px",
  padding: "8px 12px",
};

const activeBadge: React.CSSProperties = {
  marginLeft: "8px",
  fontSize: "0.7rem",
  padding: "2px 6px",
  borderRadius: "4px",
  background: "#ecfdf5",
  color: "#065f46",
};

const rollbackBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: "0.75rem",
  color: "#0f7b8a",
  padding: 0,
};

export default UseCaseEditor;
